/**
 * HTTP client for the reference Django hoodie API (session auth, CSRF).
 * Matches reference frontend (hoodie-client) pattern: credentials include, X-CSRFToken for non-GET.
 */

import { HOODIE_API_BASE_URL, isHoodieApiConfigured } from "./config";

const CSRF_COOKIE_NAME = "csrf_token";

export function getCsrfToken() {
  if (typeof document === "undefined" || !document.cookie) return null;
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + CSRF_COOKIE_NAME + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * @param {string} path - Path relative to base (e.g. "listings/" or "tags/")
 * @param {RequestInit} [options] - fetch options (method, body, etc.)
 * @returns {Promise<Response>}
 */
export async function hoodieFetch(path, options = {}) {
  if (!isHoodieApiConfigured()) {
    throw new Error(
      "REACT_APP_HOODIE_API_BASE_URL is not set; cannot call hoodie API"
    );
  }

  const url = path.startsWith("http") ? path : HOODIE_API_BASE_URL + path;
  const method = (options.method || "GET").toUpperCase();
  const isMutating = !/^(GET|HEAD|OPTIONS|TRACE)$/.test(method);

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");
  if (options.body && typeof options.body === "string") {
    headers.set("Content-Type", "application/json");
  }
  if (isMutating) {
    const csrf = getCsrfToken();
    if (csrf) headers.set("X-CSRFToken", csrf);
  }

  // Use credentials: "omit" for GET so CORS works when backend sends Access-Control-Allow-Origin: *
  // (browsers forbid credentials + wildcard). Listings, tag-groupings, events, promos allow anonymous read.
  const fetchOpts = {
    ...options,
    method,
    headers,
    credentials: isMutating ? "include" : "omit",
  };

  return fetch(url, fetchOpts);
}

/**
 * Session-aware fetch — always sends credentials (session cookies).
 * Use for authenticated reads (e.g. hoodies/me/) where GET must include cookies.
 * Mutating requests still attach X-CSRFToken when available.
 */
export async function hoodieFetchSession(path, options = {}) {
  if (!isHoodieApiConfigured()) {
    throw new Error(
      "REACT_APP_HOODIE_API_BASE_URL is not set; cannot call hoodie API"
    );
  }

  const url = path.startsWith("http") ? path : HOODIE_API_BASE_URL + path;
  const method = (options.method || "GET").toUpperCase();
  const isMutating = !/^(GET|HEAD|OPTIONS|TRACE)$/.test(method);

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");
  if (options.body && typeof options.body === "string") {
    headers.set("Content-Type", "application/json");
  }
  if (isMutating) {
    const csrf = getCsrfToken();
    if (csrf) headers.set("X-CSRFToken", csrf);
  }

  return fetch(url, {
    ...options,
    method,
    headers,
    credentials: "include",
  });
}

/**
 * Parse JSON from hoodieFetchSession; does not throw on 401/403 (caller interprets).
 * @returns {Promise<{ data: any, response: Response, ok: boolean }>}
 */
export async function hoodieFetchSessionJson(path, options = {}) {
  const response = await hoodieFetchSession(path, options);
  const contentType = response.headers.get("content-type");
  const data =
    contentType && contentType.includes("application/json")
      ? await response.json()
      : null;

  return { data, response, ok: response.ok };
}

/**
 * Same as hoodieFetch but parse JSON and reject on non-ok by default.
 * @returns {Promise<{ data: any, response: Response }>}
 */
export async function hoodieFetchJson(path, options = {}) {
  const response = await hoodieFetch(path, options);
  const contentType = response.headers.get("content-type");
  const data =
    contentType && contentType.includes("application/json")
      ? await response.json()
      : null;

  if (!response.ok) {
    const err = new Error(
      data?.detail || data?.message || `HTTP ${response.status}`
    );
    err.status = response.status;
    err.data = data;
    err.response = response;
    throw err;
  }

  return { data, response };
}

export { HOODIE_API_BASE_URL, isHoodieApiConfigured };
